import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconShoppingCart } from '@redteclab/icons'

import { LoadingButton } from 'base-ui'

import { useDeviceDetectorContext } from '../../../../../device-detector'
import { experimentsCompeteraExperimentIsEnabled } from '../../../../../experiments/competera-experiment/experimentsCompeteraExperiment'
import { useDeliveryInfoExperiment } from '../../../../../experiments/components/ExperimentDeliveryDatesOTC/ExperimentDeliveryDatesOtc'
import { useGetFreeGiftPromotionsExperiment } from '../../../../../experiments/components/ExperimentFreeGiftPromotion/useGetFreeGiftPromotionsExperiment'
import { useExperimentsContext } from '../../../../../experiments/components/ExperimentsContext'
import {
  EXPERIMENTS_TRACK_EVENT_ADD_TO_CART_BUTTON,
  EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA,
  EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_MP_PRODUCT,
  EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_MP_PRODUCT_WITH_SELLER_PROMOTIONS,
  EXPERIMENTS_TRACK_EVENT_PDP_COMPETERA,
  experimentsTrackEvent,
} from '../../../../../experiments/eventTracking'
import { useGetExperimentForTenant } from '../../../../../experiments/hooks'
import { EXPERIMENT_HEADER_STICKY_TOGGLE } from '../../../../../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { experimentAccessorIsEnabled } from '../../../../../experiments/model/experimentAccessor'
import { useGlobalConfigContext } from '../../../../../global-config'
import { productAccessorIsSaeOffer } from '../../../../model/productAccessor/productAccessorIsSaeOffer'
import { productAccessorOfferHasSellerPromotion } from '../../../../model/productAccessor/productAccessorOfferHasSellerPromotion'
import { useProductPromotion } from '../../../ProductDescription/ProductDescriptionPromotions'
import { useProductPageContext } from '../../../productPageContext'

import styles from './ProductAddToCartCta.module.css'

const useTrackAddToCartEvents = (): {
  trackAddToCartButton: () => void
  trackAddToCartButtonGenericEvent: () => void
  trackAddToCartButtonMarketPlaceProduct: () => void
  trackCompeteraAddToCartEvent: () => void
} => {
  const globalConfig = useGlobalConfigContext()
  const { experiments } = useExperimentsContext()
  const { currentOffer } = useProductPageContext()
  const { hasPromotion } = useProductPromotion()
  const { isMobile } = useDeviceDetectorContext()
  const { deliveryDatesExperiment, isOTProduct } = useDeliveryInfoExperiment()
  const freeGiftPromotionsExperiment =
    useGetFreeGiftPromotionsExperiment(isMobile)
  const headerStickyToggleExperiment = useGetExperimentForTenant(
    EXPERIMENT_HEADER_STICKY_TOGGLE,
  )
  const isHeaderStickyToggleExperiment = experimentAccessorIsEnabled(
    headerStickyToggleExperiment.name,
    experiments,
  )

  const trackCompeteraAddToCartEvent = (): void => {
    if (experimentsCompeteraExperimentIsEnabled(experiments)) {
      experimentsTrackEvent(globalConfig, EXPERIMENTS_TRACK_EVENT_PDP_COMPETERA)
    }
  }

  const trackAddToCartButtonMarketPlaceProduct = (): void => {
    const isMarketPlace =
      currentOffer && !productAccessorIsSaeOffer(currentOffer)
    const productOfferHasSellerPromotion =
      productAccessorOfferHasSellerPromotion(currentOffer)

    if (isMarketPlace) {
      experimentsTrackEvent(
        globalConfig,
        EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_MP_PRODUCT,
      )

      if (productOfferHasSellerPromotion) {
        experimentsTrackEvent(
          globalConfig,
          EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_MP_PRODUCT_WITH_SELLER_PROMOTIONS,
        )
      }
    }
  }

  const trackAddToCartButtonGenericEvent = (): void => {
    if (
      (hasPromotion && freeGiftPromotionsExperiment.isEnabled) ||
      (deliveryDatesExperiment.isEnabled && isOTProduct)
    ) {
      experimentsTrackEvent(
        globalConfig,
        EXPERIMENTS_TRACK_EVENT_ADD_TO_CART_BUTTON,
      )
    }
  }

  const trackAddToCartButton = (): void => {
    if (isHeaderStickyToggleExperiment) {
      experimentsTrackEvent(
        globalConfig,
        EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA,
      )
    }
  }

  return {
    trackAddToCartButton,
    trackAddToCartButtonGenericEvent,
    trackAddToCartButtonMarketPlaceProduct,
    trackCompeteraAddToCartEvent,
  }
}

type AddToCartButtonProps = {
  loading: boolean
}

export const AddToCartButton: FC<AddToCartButtonProps> = ({ loading }) => {
  const {
    trackAddToCartButton,
    trackAddToCartButtonGenericEvent,
    trackAddToCartButtonMarketPlaceProduct,
    trackCompeteraAddToCartEvent,
  } = useTrackAddToCartEvents()

  return (
    <LoadingButton
      className={clsx(
        'w-full',
        'whitespace-pre-wrap',
        styles['product-add-to-cart-cta__button'],
      )}
      data-qa-id="add-to-cart-button"
      disabled={loading}
      loading={loading}
      loadingPosition="start"
      onClick={() => {
        trackAddToCartButtonGenericEvent()
        trackAddToCartButtonMarketPlaceProduct()
        trackAddToCartButton()
        trackCompeteraAddToCartEvent()
      }}
      startIcon={loading ? null : <IconShoppingCart />}
      type="submit"
    >
      <FormattedMessage id="general.button.label.addToCart" />
    </LoadingButton>
  )
}
